import {DateTime} from "luxon";
import type {EBirdMyDataSchema} from "ebird-mydata-reader";
import {getMacaulayCatalogNumbers} from "../macaulayLibrary";
import {getFamilySlug} from "../helpers";
import {toRootRelativeImagePath} from "../imagePaths";
import {EARLY_PROFILE_MINIMUM_ACTIVE_DAYS, EARLY_PROFILE_MINIMUM_CHECKLISTS, AFFINITY_COPY, AFFINITY_FAMILIES, MINIMUM_AFFINITY_OUTINGS, MINIMUM_AFFINITY_SPECIES, PRIMARY_PERSONALITY_COPY, TRAIT_COPY} from "./constants";
import type {BirdingAffinityId, BirdingPersonalityAffinity, BirdingPersonalityProfile, BirdingPersonalityProfileInput, BirdingPersonalityPrimaryType, BirdingPersonalitySignal, BirdingPersonalityTraitId} from "./types";

type NormalizedChecklist = {
    id: string,
    date?: string,
    weekday?: number,
    hour?: number,
    durationMinutes?: number,
    locationKey: string,
    countyKey: string,
    stateProvince?: string,
    latitude?: number,
    longitude?: number,
    photoCount: number,
    speciesKeys: Set<string>,
    familyScientificNames: Set<string>,
    speciesKeysByFamily: Map<string, Set<string>>
};

const getObservationKey = (observation: EBirdMyDataSchema) => observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';
const getChecklistKey = (observation: EBirdMyDataSchema) => observation.submissionId || `${observation.date || ''}|${observation.time || ''}|${observation.locationId || observation.location || ''}`;
const getPercent = (value: number, total: number) => total ? Math.round(value / total * 100) : 0;

const getFamilyBySpeciesKey = (input: BirdingPersonalityProfileInput) => {
    const familyBySpeciesKey = new Map<string, string>();
    input.checklist.forEach(family => family.speciesList.forEach(species => {
        familyBySpeciesKey.set(species.taxonomicOrder.toString(), family.familyScientific);
        (species.alternateTaxonomicOrders ?? []).forEach(order => familyBySpeciesKey.set(order.toString(), family.familyScientific));
    }));
    return familyBySpeciesKey;
};

const normalizeChecklists = (input: BirdingPersonalityProfileInput) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(input);
    const checklists = new Map<string, NormalizedChecklist>();
    input.rawData.forEach(observation => {
        const id = getChecklistKey(observation);
        const parsedDate = DateTime.fromISO(observation.date || '');
        const item = checklists.get(id) ?? {
            id,
            date: observation.date,
            weekday: parsedDate.isValid ? parsedDate.weekday : undefined,
            hour: observation.time ? DateTime.fromISO(`${observation.date}T${observation.time}`).hour : undefined,
            durationMinutes: Number(observation.durationMin) || undefined,
            locationKey: observation.locationId || observation.location || '',
            countyKey: observation.stateProvince && observation.county ? `${observation.stateProvince}|${observation.county}` : '',
            stateProvince: observation.stateProvince,
            latitude: Number.isFinite(Number(observation.latitude)) ? Number(observation.latitude) : undefined,
            longitude: Number.isFinite(Number(observation.longitude)) ? Number(observation.longitude) : undefined,
            photoCount: 0,
            speciesKeys: new Set<string>(),
            familyScientificNames: new Set<string>(),
            speciesKeysByFamily: new Map<string, Set<string>>()
        };
        const speciesKey = getObservationKey(observation);
        if (speciesKey) item.speciesKeys.add(speciesKey);
        const family = familyBySpeciesKey.get(speciesKey);
        if (family) {
            item.familyScientificNames.add(family);
            const familySpecies = item.speciesKeysByFamily.get(family) ?? new Set<string>();
            if (speciesKey) familySpecies.add(speciesKey);
            item.speciesKeysByFamily.set(family, familySpecies);
        }
        item.photoCount += getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length;
        checklists.set(id, item);
    });
    return Array.from(checklists.values()).filter(checklist => Boolean(checklist.date));
};

const getTopKey = (values: Array<string | number | undefined>) => {
    const counts = new Map<string | number, number>();
    values.forEach(value => { if (value !== undefined && value !== '') counts.set(value, (counts.get(value) ?? 0) + 1); });
    return Array.from(counts.entries()).sort((left, right) => right[1] - left[1])[0]?.[0];
};

const createSignal = (id: BirdingPersonalityTraitId, score: number): BirdingPersonalitySignal => ({id, score, ...TRAIT_COPY[id]});

const getHomeChecklistPercent = (checklists: NormalizedChecklist[], input: BirdingPersonalityProfileInput) => {
    const home = input.homeLocation;
    if (!home || !Number.isFinite(home.latitude) || !Number.isFinite(home.longitude)) return undefined;
    const withinHomeRange = checklists.filter(checklist => {
        if (checklist.latitude === undefined || checklist.longitude === undefined) return false;
        const latitudeDelta = (checklist.latitude - home.latitude) * Math.PI / 180;
        const longitudeDelta = (checklist.longitude - home.longitude) * Math.PI / 180;
        const haversine = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(home.latitude * Math.PI / 180) * Math.cos(checklist.latitude * Math.PI / 180) * Math.sin(longitudeDelta / 2) ** 2;
        return 3958.8 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)) <= 20;
    });
    return getPercent(withinHomeRange.length, checklists.length);
};

const getAffinities = (checklists: NormalizedChecklist[]): BirdingPersonalityAffinity[] => {
    const familyAffinities = (Object.keys(AFFINITY_FAMILIES) as Array<Exclude<BirdingAffinityId, 'migration-watcher'>>).flatMap(id => {
        const families = new Set(AFFINITY_FAMILIES[id]);
        const qualifying = checklists.filter(checklist => Array.from(checklist.familyScientificNames).some(family => families.has(family)));
        const species = new Set(qualifying.flatMap(checklist => Array.from(families).flatMap(family => Array.from(checklist.speciesKeysByFamily.get(family) ?? []))));
        if (qualifying.length < MINIMUM_AFFINITY_OUTINGS || species.size < MINIMUM_AFFINITY_SPECIES) return [];
        return [{id, ...AFFINITY_COPY[id], speciesCount: species.size, outingCount: qualifying.length}];
    });
    const migrationMonths = new Set([3, 4, 5, 8, 9, 10]);
    const migrationChecklists = checklists.filter(checklist => migrationMonths.has(DateTime.fromISO(checklist.date || '').month));
    const migrationSpecies = new Set(migrationChecklists.flatMap(checklist => Array.from(checklist.speciesKeys)));
    const migrationAffinity = migrationChecklists.length >= MINIMUM_AFFINITY_OUTINGS && migrationSpecies.size >= MINIMUM_AFFINITY_SPECIES && getPercent(migrationChecklists.length, checklists.length) >= 55
        ? [{id: 'migration-watcher' as const, ...AFFINITY_COPY['migration-watcher'], speciesCount: migrationSpecies.size, outingCount: migrationChecklists.length}]
        : [];
    return [...familyAffinities, ...migrationAffinity].sort((left, right) => right.outingCount - left.outingCount || right.speciesCount - left.speciesCount);
};

const buildPresentationData = (input: BirdingPersonalityProfileInput, checklists: NormalizedChecklist[], averageDurationMinutes?: number) => {
    const monthlyChecklistCounts = Array.from({length: 12}, (_, index) => ({
        month: index + 1,
        checklistCount: checklists.filter(checklist => DateTime.fromISO(checklist.date || '').month === index + 1).length
    }));
    const speciesByYear = new Map<number, Set<string>>();
    const speciesSummary = new Map<string, {label: string, firstObservedDate?: string, observationCount: number, photoCount: number, taxonomicOrder?: number}>();
    const familyBySpeciesKey = getFamilyBySpeciesKey(input);
    const familyLabelByScientificName = new Map(input.checklist.map(family => [family.familyScientific, family.familyCommon]));
    const speciesByFamily = new Map<string, Set<string>>();
    input.rawData.forEach(observation => {
        const speciesKey = getObservationKey(observation);
        const year = DateTime.fromISO(observation.date || '').year;
        if (speciesKey && Number.isFinite(year)) {
            const species = speciesByYear.get(year) ?? new Set<string>();
            species.add(speciesKey);
            speciesByYear.set(year, species);
        }
        if (!speciesKey) return;
        const familyScientificName = familyBySpeciesKey.get(speciesKey);
        if (familyScientificName) {
            const species = speciesByFamily.get(familyScientificName) ?? new Set<string>();
            species.add(speciesKey);
            speciesByFamily.set(familyScientificName, species);
        }
        const existing = speciesSummary.get(speciesKey) ?? {
            label: observation.commonName || observation.scientificName || 'Unknown species',
            firstObservedDate: observation.date,
            observationCount: 0,
            photoCount: 0,
            taxonomicOrder: observation.taxonomicOrder
        };
        if (observation.date && (!existing.firstObservedDate || observation.date < existing.firstObservedDate)) existing.firstObservedDate = observation.date;
        existing.observationCount += 1;
        existing.photoCount += getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length;
        speciesSummary.set(speciesKey, existing);
    });
    const yearlySpeciesCounts = Array.from(speciesByYear.entries())
        .sort((left, right) => left[0] - right[0])
        .map(([year, species]) => ({year, speciesCount: species.size}));
    const signatureSpeciesMetadata = new Map<string, {image?: string, detailPath?: string}>();
    input.checklist.forEach(family => family.speciesList.forEach(species => {
        const detailPath = `/family-explorer/family/${getFamilySlug(family.familyName)}/species/${species.speciesCode}`;
        const image = toRootRelativeImagePath(species.image?.thumb || input.speciesImages?.[species.taxonomicOrder]?.thumb);
        signatureSpeciesMetadata.set(species.taxonomicOrder.toString(), {image, detailPath});
        (species.alternateTaxonomicOrders ?? []).forEach(order => signatureSpeciesMetadata.set(order.toString(), {image, detailPath}));
    }));
    const signatureSpecies = Array.from(speciesSummary.entries())
        .sort((left, right) => right[1].observationCount - left[1].observationCount || right[1].photoCount - left[1].photoCount)
        .slice(0, 6)
        .map(([speciesKey, species]) => ({speciesKey, ...species, ...signatureSpeciesMetadata.get(speciesKey)}));
    const topFamilyCounts = Array.from(speciesByFamily.entries())
        .map(([familyScientificName, species]) => ({label: familyLabelByScientificName.get(familyScientificName) || familyScientificName, count: species.size}))
        .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
        .slice(0, 6);
    const funFacts = [
        averageDurationMinutes ? `Your typical timed outing lasts ${averageDurationMinutes.toLocaleString()} minutes.` : undefined,
        checklists.length ? `Your largest checklist includes ${Math.max(...checklists.map(checklist => checklist.speciesKeys.size)).toLocaleString()} species.` : undefined
    ].filter((fact): fact is string => Boolean(fact));
    return {monthlyChecklistCounts, yearlySpeciesCounts, funFacts, signatureSpecies, topFamilyCounts};
};

export const buildBirdingPersonalityProfile = (input: BirdingPersonalityProfileInput): BirdingPersonalityProfile => {
    const checklists = normalizeChecklists(input);
    const activeDays = new Set(checklists.map(checklist => checklist.date)).size;
    const activeYears = new Set(checklists.map(checklist => checklist.date?.slice(0, 4)).filter(Boolean)).size;
    const speciesCount = new Set(input.lifeList.map(getObservationKey).filter(Boolean)).size;
    const locationCount = new Set(checklists.map(checklist => checklist.locationKey).filter(Boolean)).size;
    const countyCount = new Set(checklists.map(checklist => checklist.countyKey).filter(Boolean)).size;
    const stateProvinceCount = new Set(checklists.map(checklist => checklist.stateProvince).filter(Boolean)).size;
    const weekendPercent = getPercent(checklists.filter(checklist => checklist.weekday === 6 || checklist.weekday === 7).length, checklists.length);
    const photoOutingPercent = getPercent(checklists.filter(checklist => checklist.photoCount > 0).length, checklists.length);
    const timedChecklists = checklists.filter(checklist => checklist.hour !== undefined);
    const durationChecklists = checklists.filter(checklist => checklist.durationMinutes);
    const averageDurationMinutes = durationChecklists.length ? Math.round(durationChecklists.reduce((total, checklist) => total + (checklist.durationMinutes ?? 0), 0) / durationChecklists.length) : undefined;
    const homeChecklistPercent = getHomeChecklistPercent(checklists, input);
    const topWeekday = getTopKey(checklists.map(checklist => checklist.weekday));
    const topHour = getTopKey(timedChecklists.map(checklist => checklist.hour));
    const weekdayLabels = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const traits: BirdingPersonalitySignal[] = [];
    if (locationCount >= 10) traits.push(createSignal('explorer', Math.min(100, locationCount * 3)));
    if (activeYears >= 2 && activeDays >= 12) traits.push(createSignal('consistent', Math.min(100, activeYears * 20 + activeDays)));
    if (speciesCount >= 50) traits.push(createSignal('diverse', Math.min(100, speciesCount / 5)));
    if (photoOutingPercent >= 25) traits.push(createSignal('photographer', photoOutingPercent));
    if (weekendPercent >= 60) traits.push(createSignal('planner', weekendPercent));
    if (timedChecklists.length >= 8 && typeof topHour === 'number' && topHour < 9) traits.push(createSignal('early-bird', getPercent(timedChecklists.filter(checklist => (checklist.hour ?? 24) < 9).length, timedChecklists.length)));
    if (homeChecklistPercent !== undefined && homeChecklistPercent >= 50) traits.push(createSignal('home-patch-regular', homeChecklistPercent));
    if (checklists.some(checklist => checklist.speciesKeys.size >= 25)) traits.push(createSignal('big-day-finder', Math.max(...checklists.map(checklist => checklist.speciesKeys.size))));
    const seasonCounts = new Set(checklists.map(checklist => Math.ceil(DateTime.fromISO(checklist.date || '').month / 3)).filter(Boolean));
    if (seasonCounts.size === 4) traits.push(createSignal('all-season-birder', 100));
    const isEarlyProfile = checklists.length < EARLY_PROFILE_MINIMUM_CHECKLISTS || activeDays < EARLY_PROFILE_MINIMUM_ACTIVE_DAYS;
    const primaryScores = ([
        {id: 'dedicated-explorer', score: Math.min(100, activeYears * 15 + locationCount * 2 + speciesCount / 10)},
        {id: 'local-naturalist', score: homeChecklistPercent ?? 0},
        {id: 'trailblazer', score: Math.min(100, stateProvinceCount * 20 + countyCount / 2 + locationCount)},
        {id: 'seasonal-seeker', score: Math.max(...[1, 2, 3, 4].map(season => getPercent(checklists.filter(checklist => Math.ceil(DateTime.fromISO(checklist.date || '').month / 3) === season).length, checklists.length)))},
        {id: 'dawn-patroller', score: timedChecklists.length >= 8 ? getPercent(timedChecklists.filter(checklist => (checklist.hour ?? 24) < 9).length, timedChecklists.length) : 0},
        {id: 'weekend-wanderer', score: weekendPercent},
        {id: 'field-photographer', score: photoOutingPercent},
        {id: 'marathon-birder', score: averageDurationMinutes && averageDurationMinutes >= 90 ? Math.min(100, averageDurationMinutes) : 0},
        {id: 'big-day-chaser', score: Math.min(100, Math.max(0, ...checklists.map(checklist => checklist.speciesKeys.size)) * 3)}
    ] as Array<{id: BirdingPersonalityPrimaryType, score: number}>).sort((left, right) => right.score - left.score);
    const winner = primaryScores[0];
    const runnerUp = primaryScores[1];
    const hasDecisivePrimary = winner.score >= 40 && winner.score - runnerUp.score >= 8;
    const primary = isEarlyProfile
        ? undefined
        : hasDecisivePrimary
            ? {id: winner.id, confidence: winner.score, ...PRIMARY_PERSONALITY_COPY[winner.id]}
            : {
                id: 'well-rounded-birder' as const,
                confidence: winner.score,
                ...PRIMARY_PERSONALITY_COPY['well-rounded-birder']
            };
    const presentationData = buildPresentationData(input, checklists, averageDurationMinutes);
    const rankedTraits = [...traits].sort((left, right) => right.score - left.score);
    return {
        isEarlyProfile,
        primary,
        directionalSigns: rankedTraits.slice(0, 3),
        traits: rankedTraits.slice(0, 5),
        affinities: getAffinities(checklists).slice(0, 3),
        stats: {
            checklistCount: checklists.length,
            activeYears,
            speciesCount,
            locationCount,
            countyCount,
            stateProvinceCount,
            mostActiveWeekday: typeof topWeekday === 'number' ? weekdayLabels[topWeekday] : undefined,
            mostActiveHourLabel: typeof topHour === 'number' ? `${topHour === 0 ? 12 : topHour > 12 ? topHour - 12 : topHour}:00 ${topHour >= 12 ? 'PM' : 'AM'}` : undefined,
            weekendPercent,
            photoOutingPercent,
            averageDurationMinutes,
            homeChecklistPercent
        },
        ...presentationData
    };
};
